import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ShoppingCart, User, LogOut, Package, Heart } from "lucide-react";
import { useWishlistStore } from "@/stores/wishlistStore";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useCartStore } from "@/stores/cartStore";
import { useAuth } from "@/hooks/useAuth";
// Optimized WebP logo at 160w (2x for retina) for header display
import logo from "@/assets/logo.png?w=160&format=webp";
import logoFallback from "@/assets/logo.png?w=160";
import { toast } from "sonner";

interface HeaderProps {
  showIntro?: boolean;
}

const Header = ({ showIntro = false }: HeaderProps) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const { setOpen, getTotalItems } = useCartStore();
  const { items: wishlistItems, setOpen: setWishlistOpen } = useWishlistStore();
  const { user, signOut } = useAuth();
  const totalItems = getTotalItems();
  const wishlistCount = wishlistItems.length;

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSignOut = async () => {
    const { error } = await signOut();
    if (error) {
      toast.error("Failed to sign out");
    } else {
      toast.success("Signed out successfully");
    }
  };

  return (
    <header 
      className={`sticky top-0 z-50 transition-all duration-300 border-b border-border/50 ${
        isScrolled 
          ? 'bg-[#EDF8F9]/95 backdrop-blur-md shadow-soft' 
          : 'bg-[#EDF8F9]'
      } ${showIntro ? 'intro-header' : ''}`}
    >
      <div className="container mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between h-20 md:h-24">
          {/* Left side - Navigation buttons */}
          <nav className="flex items-center gap-2 flex-1">
            <Link 
              to="/models" 
              className="relative text-sm md:text-base font-semibold text-primary-foreground px-3 md:px-4 py-2 rounded-full bg-primary hover:scale-105 active:scale-95 transition-all duration-300 ease-out shadow-md hover:shadow-lg"
            >
              Models
            </Link>
            <Link 
              to="/collections" 
              className="relative text-sm md:text-base font-medium text-foreground hover:text-primary px-3 py-2 transition-colors hidden sm:block"
            >
              Collections
            </Link>
          </nav>
          
          {/* Center - Logo */}
          <Link to="/" className={`flex items-center gap-2 absolute left-1/2 -translate-x-1/2 ${showIntro ? 'intro-logo' : ''}`}>
            <picture>
              <source srcSet={logo} type="image/webp" />
              <img src={logoFallback} alt="Funzy Feets" className="h-16 md:h-20 w-auto" width={80} height={80} fetchPriority="high" decoding="async" />
            </picture>
          </Link>
          
          {/* Right side - Wishlist, Auth, Cart and Shop Now */}
          <nav className="flex items-center gap-3 flex-1 justify-end">
            {/* Wishlist Button - always visible */}
            <Button
              variant="ghost"
              size="icon"
              className="relative h-9 w-9 md:h-10 md:w-10 min-w-[36px] min-h-[36px]"
              onClick={() => setWishlistOpen(true)}
              title="Wishlist"
            >
              <Heart className={`h-4 w-4 md:h-5 md:w-5 ${wishlistCount > 0 ? 'fill-red-500 text-red-500' : ''}`} />
              {wishlistCount > 0 && (
                <Badge className="absolute -top-1 -right-1 h-4 w-4 md:h-5 md:w-5 rounded-full p-0 flex items-center justify-center text-[10px] md:text-xs bg-red-500 text-white">
                  {wishlistCount}
                </Badge>
              )}
            </Button>
            
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-9 w-9 md:h-10 md:w-10 min-w-[36px] min-h-[36px]" title="Account">
                    <User className="h-4 w-4 md:h-5 md:w-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-background">
                  <DropdownMenuItem asChild>
                    <Link to="/orders" className="flex items-center gap-2">
                      <Package className="h-4 w-4" />
                      Order History
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleSignOut} className="flex items-center gap-2">
                    <LogOut className="h-4 w-4" />
                    Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Link to="/auth">
                <Button variant="ghost" size="icon" className="h-9 w-9 md:h-10 md:w-10 min-w-[36px] min-h-[36px]" title="Sign In">
                  <User className="h-4 w-4 md:h-5 md:w-5" />
                </Button>
              </Link>
            )}
            <Button
              variant="ghost"
              size="icon"
              className="relative h-9 w-9 md:h-10 md:w-10 min-w-[36px] min-h-[36px]"
              onClick={() => setOpen(true)}
              aria-label="Shopping cart"
              title="Shopping cart"
            >
              <ShoppingCart className="h-4 w-4 md:h-5 md:w-5" />
              {totalItems > 0 && (
                <Badge className="absolute -top-1 -right-1 h-4 w-4 md:h-5 md:w-5 rounded-full p-0 flex items-center justify-center text-[10px] md:text-xs bg-accent text-accent-foreground">
                  {totalItems}
                </Badge>
              )}
            </Button>
            <Link 
              to="/product" 
              className={`relative text-xs md:text-base font-semibold text-primary-foreground px-3 md:px-4 py-2 md:py-2 rounded-full bg-primary hover:scale-105 active:scale-95 transition-all duration-300 ease-out shadow-md hover:shadow-lg min-h-[36px] flex items-center ${showIntro ? 'intro-cta' : ''}`}
            >
              Shop Now
            </Link>
          </nav>
        </div>
      </div>
    </header>
  );
};

export default Header;
